import { useEffect, useState, useRef } from 'react'
import styles from './DarkModeExtensionWarning.module.scss'

const BLAZE_FONT_ATTR = 'data-blaze-font'
const CONSECUTIVE_DETECTIONS_REQUIRED = 3
const INITIAL_CHECK_DELAY_MS = 2000
const CHECK_INTERVAL_MS = 1000

/** Returns true if the stylesheet is a first-party Blaze font stylesheet (should not trigger warning). */
function isBlazeFontStylesheet(sheet: CSSStyleSheet): boolean {
  try {
    const ownerNode = sheet.ownerNode as HTMLElement | null
    if (ownerNode?.getAttribute(BLAZE_FONT_ATTR) === 'true') return true
  } catch {
    // ignore
  }
  try {
    const href = sheet.href || ''
    if (href.includes('blaze_fonts')) return true
    if (href.startsWith('/') || href.startsWith('//')) return true
    if (href) {
      try {
        // eslint-disable-next-line no-restricted-properties
        const currentOrigin = document.location.origin
        if (new URL(href, currentOrigin).origin === currentOrigin) return true
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }
  return false
}

/** Parses an RGB/hex color string and returns its perceived brightness (0–255), or null if unparseable. */
function getColorBrightness(color: string): number | null {
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch.map(Number)
    return (r * 299 + g * 587 + b * 114) / 1000
  }
  const hexMatch = color.match(/#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})/)
  if (hexMatch) {
    const [, r, g, b] = hexMatch.map((h) => parseInt(h, 16))
    return (r * 299 + g * 587 + b * 114) / 1000
  }
  return null
}

/**
 * Detects if dark mode extensions are active and shows a helpful message
 * asking users to disable them for this site.
 */
export const DarkModeExtensionWarning = () => {
  const [showWarning, setShowWarning] = useState(false)
  const [detectedExtension, setDetectedExtension] = useState<string | null>(null)
  const [userDismissed, setUserDismissed] = useState(false)
  // Track consecutive positive detections to avoid false positives during page load
  const consecutiveDetectionsRef = useRef(0)

  useEffect(() => {
    // Check if stylesheets are loaded to avoid false positives during page load
    const areStylesLoaded = () => {
      try {
        const stylesheets = Array.from(document.styleSheets)
        // If we can't access cssRules, stylesheets might not be fully loaded
        return stylesheets.every((sheet) => {
          try {
            // Try to access cssRules - if it throws, stylesheet might not be loaded
            // If it's null, stylesheet might not be loaded yet
            return sheet.cssRules !== null || sheet.href === null
          } catch {
            // Cross-origin stylesheets will throw - assume they're loaded
            return true
          }
        })
      } catch {
        // If we can't check stylesheets, assume they're loaded
        return true
      }
    }

    // Check for dark mode extensions
    const checkDarkModeExtensions = () => {
      // Check if we have darkreader-lock meta tag (Dark Reader is handled programmatically)
      const hasDarkReaderLock = document.querySelector('meta[name="darkreader-lock"]') !== null

      // Check for Dark Reader (most common) - specific detection
      const hasDarkReader =
        document.documentElement.hasAttribute('data-darkreader-mode') ||
        document.documentElement.hasAttribute('data-darkreader-scheme') ||
        document.querySelector('[data-darkreader-mode]') !== null ||
        // Check for Dark Reader's injected styles
        Array.from(document.styleSheets).some((sheet) => {
          try {
            return sheet.href?.includes('darkreader') || false
          } catch {
            return false
          }
        })

      // Dark Reader is handled programmatically via darkreader-lock meta tag
      // If Dark Reader is detected, we know it's handled, so we can skip showing warning for it
      // BUT: we still need to check for OTHER extensions
      // If only the lock tag exists (Dark Reader not active), we should be more careful about false positives

      // Generic detection for other dark mode extensions
      // Check for common patterns that indicate dark mode extensions

      // 1. Check for filter-based darkening (common pattern)
      const bodyStyle = window.getComputedStyle(document.body)
      const htmlStyle = window.getComputedStyle(document.documentElement)
      const bodyFilter = bodyStyle.filter
      const htmlFilter = htmlStyle.filter
      const hasFilter = (bodyFilter !== 'none' && bodyFilter !== '') || (htmlFilter !== 'none' && htmlFilter !== '')

      // Find main container once to reuse for multiple checks
      const mainContainer = document.querySelector(
        '[class*="container"], [class*="page"], [class*="root"], [class*="presenter"], [class*="mounter"]',
      )

      // Also check for filter on any major container
      let hasContainerFilter = false
      if (mainContainer) {
        const containerStyle = window.getComputedStyle(mainContainer)
        hasContainerFilter = containerStyle.filter !== 'none' && containerStyle.filter !== ''
      }
      const hasFilterFinal = hasFilter || hasContainerFilter

      // 2. Check for injected stylesheets from extensions
      // Excludes Dark Reader (handled separately) and Blaze font stylesheets
      const hasExtensionStylesheet = Array.from(document.styleSheets).some((sheet) => {
        try {
          if (isBlazeFontStylesheet(sheet)) return false
          const href = sheet.href || ''
          if (href.includes('darkreader')) return false
          return (
            href.includes('extension://') ||
            href.includes('moz-extension://') ||
            href.includes('chrome-extension://') ||
            href.includes('safari-extension://') ||
            href.includes('dark') ||
            href.includes('mode') ||
            href.includes('theme')
          )
        } catch {
          // Cross-origin stylesheets may throw when accessing href
          try {
            if (isBlazeFontStylesheet(sheet)) return false
            const ownerNode = sheet.ownerNode as HTMLElement | null
            if (ownerNode) {
              const id = ownerNode.id || ''
              const className = String(ownerNode.className ?? '')
              return id.includes('extension') || className.includes('extension')
            }
          } catch {
            // ignore
          }
          return false
        }
      })

      // 3. Check for dark backgrounds when we expect light
      // If body/html has a dark background color, it might be a dark mode extension
      const bodyBg = bodyStyle.backgroundColor
      const htmlBg = htmlStyle.backgroundColor
      const isDarkBackground = (color: string) => {
        if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return false
        const brightness = getColorBrightness(color)
        if (brightness !== null) return brightness < 150
        // Fallback: check for named colors that are dark
        const darkNamedColors = ['black', 'navy', 'darkblue', 'dark', 'darkslategray', 'darkslategrey']
        return darkNamedColors.some((dark) => color.toLowerCase().trim().includes(dark))
      }

      const hasDarkBg = isDarkBackground(bodyBg) || isDarkBackground(htmlBg)

      // Also check the main content container if it exists (reuse mainContainer from above)
      let hasDarkContainerBg = false
      if (mainContainer) {
        const containerStyle = window.getComputedStyle(mainContainer)
        hasDarkContainerBg = isDarkBackground(containerStyle.backgroundColor)
      }

      // 4. Check for common data attributes that extensions might add
      // Exclude Dark Reader attributes since we handle it separately
      const hasExtensionAttributes =
        Array.from(document.documentElement.attributes).some((attr) => {
          const name = attr.name.toLowerCase()
          // Exclude Dark Reader attributes
          if (name.includes('darkreader')) return false
          return name.includes('dark') || name.includes('mode') || name.includes('theme') || name.includes('extension')
        }) ||
        (document.querySelector('[data-dark-mode]') !== null && !document.querySelector('[data-darkreader-mode]')) ||
        (document.querySelector('[data-theme="dark"]') !== null && !hasDarkReader)

      // 5. Check if text color is unexpectedly light (inverted)
      const bodyColor = bodyStyle.color
      const isLightText = (color: string) => {
        const brightness = getColorBrightness(color)
        return brightness !== null && brightness > 200
      }

      const hasLightText = isLightText(bodyColor)

      // Combine checks: if multiple indicators suggest dark mode extension
      // But only if we don't have darkreader-lock (meaning Dark Reader is handled)
      const indicators = [
        hasFilterFinal,
        hasExtensionStylesheet,
        hasDarkBg || hasDarkContainerBg,
        hasExtensionAttributes,
        hasLightText,
      ]
      const indicatorCount = indicators.filter(Boolean).length

      // Lower threshold: if we have 1+ strong indicators, it's likely a dark mode extension
      // Strong indicators are: filter, extension stylesheet, or dark background
      const strongIndicators = [hasFilterFinal, hasExtensionStylesheet, hasDarkBg || hasDarkContainerBg]
      const hasStrongIndicator = strongIndicators.some(Boolean)

      // Very aggressive: show warning if we have ANY indicator
      // Only skip if Dark Reader is actually detected (not just if lock tag exists)
      // The lock tag only disables Dark Reader, not other extensions
      // BUT: if lock tag exists, require stronger evidence (2+ indicators) to avoid false positives
      // When lock tag exists, Dark Reader is disabled, so weak indicators might be false positives
      // Also: if Dark Reader is detected, don't show warning (it's handled)
      const shouldShow =
        !hasDarkReader &&
        !userDismissed &&
        (hasStrongIndicator ||
          (indicatorCount >= 1 && !hasDarkReaderLock) ||
          (indicatorCount >= 2 && hasDarkReaderLock))

      // Additional safeguard: if Dark Reader lock exists and we only have weak indicators,
      // it's likely a false positive from Dark Reader's disabled state
      const isLikelyFalsePositive = hasDarkReaderLock && !hasStrongIndicator && indicatorCount < 2

      const finalShouldShow = shouldShow && !isLikelyFalsePositive

      if (finalShouldShow) {
        // Require 3 consecutive positive detections to avoid false positives during page load
        consecutiveDetectionsRef.current++
        if (consecutiveDetectionsRef.current >= CONSECUTIVE_DETECTIONS_REQUIRED) {
          setDetectedExtension('a dark mode extension')
          setShowWarning(true)
        }
      } else {
        consecutiveDetectionsRef.current = 0
        setShowWarning(false)
        // Reset dismiss state once the extension is truly gone
        if (!hasStrongIndicator && indicatorCount === 0 && userDismissed) {
          setUserDismissed(false)
        }
      }
    }

    // Wait for page to stabilize before checking to avoid race conditions during page load
    // This prevents false positives when styles haven't fully loaded yet
    let initialCheckTimeout: ReturnType<typeof setTimeout>
    let interval: ReturnType<typeof setInterval>
    let loadEventListener: (() => void) | null = null
    let domContentLoadedListener: (() => void) | null = null

    const startChecking = () => {
      // Check if stylesheets are loaded, if not wait a bit more
      const checkWithStylesheetValidation = () => {
        if (areStylesLoaded()) {
          // Stylesheets are loaded, start checking
          checkDarkModeExtensions()
          // Check periodically in case extensions load later
          interval = setInterval(checkDarkModeExtensions, CHECK_INTERVAL_MS)
        } else {
          // Stylesheets not fully loaded yet, wait a bit more
          initialCheckTimeout = setTimeout(checkWithStylesheetValidation, 500)
        }
      }

      initialCheckTimeout = setTimeout(checkWithStylesheetValidation, INITIAL_CHECK_DELAY_MS)
    }

    // Wait for window.load event which fires after all resources (including stylesheets) are loaded
    // This is more reliable than DOMContentLoaded for ensuring styles are ready
    if (document.readyState === 'complete') {
      // Page is already fully loaded
      startChecking()
    } else if (document.readyState === 'loading') {
      // Page is still loading, wait for load event
      loadEventListener = () => {
        startChecking()
      }
      window.addEventListener('load', loadEventListener)

      // Also listen for DOMContentLoaded as a fallback
      domContentLoadedListener = () => {
        // If load event hasn't fired yet, start checking anyway after a delay
        // This handles cases where load event might not fire
        if (!interval) {
          startChecking()
        }
      }
      document.addEventListener('DOMContentLoaded', domContentLoadedListener)
    } else {
      // Interactive state - DOM is ready but resources might still be loading
      // Wait for load event or start checking after a delay
      loadEventListener = () => {
        startChecking()
      }
      window.addEventListener('load', loadEventListener)

      // Fallback: start checking after delay if load event doesn't fire
      initialCheckTimeout = setTimeout(() => {
        if (!interval && loadEventListener) {
          window.removeEventListener('load', loadEventListener)
          startChecking()
        }
      }, 3000)
    }

    return () => {
      clearTimeout(initialCheckTimeout)
      clearInterval(interval)
      if (loadEventListener) {
        window.removeEventListener('load', loadEventListener)
      }
      if (domContentLoadedListener) {
        document.removeEventListener('DOMContentLoaded', domContentLoadedListener)
      }
    }
  }, [userDismissed])

  if (!showWarning) return null

  // Dark Reader-specific instructions
  const getInstructions = () => {
    if (detectedExtension === 'Dark Reader') {
      return (
        <>
          <strong>To fix this:</strong> Please disable Dark Reader for this site. You can usually do this by clicking
          the Dark Reader icon in your browser toolbar and selecting "Disable for this site" or adding{' '}
          <code>app.blaze.ai</code> to your exclusion list.
        </>
      )
    }
    // Generic instructions for other extensions
    return (
      <>
        <strong>To fix this:</strong> Please disable {detectedExtension} for this site. Look for the extension icon in
        your browser toolbar and disable it for <code>app.blaze.ai</code>.
      </>
    )
  }

  return (
    <div className={styles.warning}>
      <div className={styles.content}>
        <strong>⚠️ Dark Mode Extension Detected</strong>
        <p>
          We detected that you're using {detectedExtension || 'a dark mode extension'}. Our onboarding is designed for
          light mode and may not display correctly with dark mode enabled.
        </p>
        <p>{getInstructions()}</p>
        <button
          className={styles.dismiss}
          onClick={() => {
            setUserDismissed(true)
            setShowWarning(false)
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}
